import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2, MapPin, Phone, Mail } from 'lucide-react';

const Retailers = () => {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRetailer, setEditingRetailer] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  const [formData, setFormData] = useState({
    name: '',
    shop_address: '',
    google_maps_location: '',
    contact_person: '',
    contact_designation: '',
    email: '',
    phone: '',
    status: 'prospect',
    notes: '',
    next_phone_followup: '',
    next_shop_visit: ''
  });

  useEffect(() => {
    fetchRetailers();
  }, []);

  const fetchRetailers = async () => {
    try {
      const response = await api.get('/retailers');
      setRetailers(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch retailers');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRetailer) {
        await api.put(`/retailers/${editingRetailer.id}`, formData);
        toast.success('Retailer updated successfully');
      } else {
        await api.post('/retailers', formData);
        toast.success('Retailer added successfully');
      }
      fetchRetailers();
      resetForm();
    } catch (error) {
      toast.error(editingRetailer ? 'Failed to update retailer' : 'Failed to add retailer');
    }
  };

  const handleEdit = (retailer) => {
    setEditingRetailer(retailer);
    setFormData(retailer);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this retailer?')) {
      try {
        await api.delete(`/retailers/${id}`);
        toast.success('Retailer deleted successfully');
        fetchRetailers();
      } catch (error) {
        toast.error('Failed to delete retailer');
      }
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingRetailer(null);
    setFormData({
      name: '',
      shop_address: '',
      google_maps_location: '',
      contact_person: '',
      contact_designation: '',
      email: '',
      phone: '',
      status: 'prospect',
      notes: '',
      next_phone_followup: '',
      next_shop_visit: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return <div className="container">Loading retailers...</div>;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Retailers</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} style={{ marginRight: '5px' }} />
          Add Retailer
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact Person</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Status</th>
              <th>Next Follow-up</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {retailers.map(retailer => (
              <tr key={retailer.id}>
                <td>
                  <strong>{retailer.name}</strong>
                  {retailer.shop_address && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      <MapPin size={12} style={{ marginRight: '4px' }} />
                      {retailer.shop_address}
                    </div>
                  )}
                </td>
                <td>
                  {retailer.contact_person}
                  {retailer.contact_designation && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {retailer.contact_designation}
                    </div>
                  )}
                </td>
                <td>
                  {retailer.phone && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Phone size={14} style={{ marginRight: '4px' }} />
                      {retailer.phone}
                    </div>
                  )}
                </td>
                <td>
                  {retailer.email && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Mail size={14} style={{ marginRight: '4px' }} />
                      {retailer.email}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`badge ${
                    retailer.status === 'customer' ? 'badge-success' : 'badge-warning'
                  }`}>
                    {retailer.status}
                  </span>
                </td>
                <td>
                  {retailer.next_phone_followup && (
                    <div style={{ fontSize: '12px' }}>
                      Phone: {formatDate(retailer.next_phone_followup)}
                    </div>
                  )}
                  {retailer.next_shop_visit && (
                    <div style={{ fontSize: '12px' }}>
                      Visit: {formatDate(retailer.next_shop_visit)}
                    </div>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => handleEdit(retailer)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(retailer.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#dc3545'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingRetailer ? 'Edit Retailer' : 'Add New Retailer'}
              </h2>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    className="form-control"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="prospect">Prospect</option>
                    <option value="customer">Customer</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Contact Person</label>
                  <input
                    type="text"
                    name="contact_person"
                    className="form-control"
                    value={formData.contact_person}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Designation</label>
                  <input
                    type="text"
                    name="contact_designation"
                    className="form-control"
                    value={formData.contact_designation}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-control"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Next Phone Follow-up</label>
                  <input
                    type="date"
                    name="next_phone_followup"
                    className="form-control"
                    value={formData.next_phone_followup}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Next Shop Visit</label>
                  <input
                    type="date"
                    name="next_shop_visit"
                    className="form-control"
                    value={formData.next_shop_visit}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Shop Address</label>
                <textarea
                  name="shop_address"
                  className="form-control"
                  value={formData.shop_address}
                  onChange={handleChange}
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Google Maps Location</label>
                <input
                  type="text"
                  name="google_maps_location"
                  className="form-control"
                  value={formData.google_maps_location}
                  onChange={handleChange}
                  placeholder="Paste Google Maps link"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  name="notes"
                  className="form-control"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingRetailer ? 'Update' : 'Add'} Retailer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Retailers;